import { Controller } from "@hotwired/stimulus";
import Dropzone, { DropzoneFile, DropzoneOptions } from "dropzone";
import { fireToast } from "../helpers/toast";

export default class extends Controller<HTMLElement> {
  static readonly values = {
    multiple: { type: Boolean, default: false },
    supportMimeTypes: { type: String, default: "" },
    maxSize: { type: Number, default: 5 },
    maxFile: { type: Number, default: null },
    paramName: { type: String, default: "files" },
  };

  static readonly targets = ["filesInput"];

  declare readonly multipleValue: boolean;
  declare readonly supportMimeTypesValue: string;
  declare readonly maxSizeValue: number;
  declare readonly maxFileValue: number | null;
  declare readonly paramNameValue: string;

  declare readonly hasFilesInputTarget: boolean;
  declare readonly filesInputTarget: HTMLInputElement;
  declare readonly filesInputTargets: HTMLInputElement[];

  private _dropzone: Dropzone | null = null;
  private _selectedFilesListContainer: HTMLElement | null = null;
  private readonly _megaByteToBytes = 1048576;

  private readonly _handleOnPaste = (event: ClipboardEvent) => {
    const activeElement = document.activeElement;

    if (activeElement?.matches("input, textarea, select")) {
      return;
    }

    if (!event.clipboardData?.items || !this._dropzone) return;

    const validFiles: File[] = [];
    for (const item of event.clipboardData.items) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          validFiles.push(file);
        }
      }
    }

    if (validFiles.length > 0) {
      event.preventDefault();

      for (const originalFile of validFiles) {
        const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
        const fileExtension = originalFile.type.split("/")[1] || "png";
        const newName = `screenshot-${timestamp}.${fileExtension}`;

        const newFile = new File([originalFile], newName, {
          type: originalFile.type,
          lastModified: originalFile.lastModified,
        });

        this._dropzone.addFile(newFile as DropzoneFile);
      }

      this._dropzone.emit("addedfiles");
    }
  };

  connect(): void {
    this._initialDropzone();
    document.addEventListener("paste", this._handleOnPaste);

    this._selectedFilesListContainer = document.querySelector(
      `[name="${this.paramNameValue}__selected-files-list"]`
    );
  }

  disconnect() {
    if (this._dropzone) {
      this._destroyDropzoneEvents(this._dropzone);
      this._dropzone.destroy();
    }
    document.removeEventListener("paste", this._handleOnPaste);
  }

  private _initialDropzone() {
    Dropzone.autoDiscover = false;

    const form = this.element.closest("form");
    const config: DropzoneOptions = {
      url: form?.action ?? "/",
      paramName: this.paramNameValue,
      uploadMultiple: this.multipleValue,
      acceptedFiles: this.supportMimeTypesValue,
      maxFilesize: this.maxSizeValue,
      parallelUploads: 10,
      autoProcessQueue: false,
      createImageThumbnails: false,
      previewsContainer: false,
    };

    if (this.maxFileValue) {
      config.maxFiles = this.multipleValue ? this.maxFileValue : 1;
    }

    this._dropzone = new Dropzone(this.element, config);

    this._bindDropzoneEvents(this._dropzone);
  }

  private _bindDropzoneEvents(dropzone: Dropzone) {
    dropzone.on("addedfiles", () => this._addPreviewFiles());
    dropzone.on("removedfile", (file) => this._removePreviewFile(file));
    dropzone.on("error", (_, err) => this._onError(err));
  }

  private _destroyDropzoneEvents(dropzone: Dropzone) {
    dropzone.off("addedfiles");
    dropzone.off("removedfile");
    dropzone.off("error");
  }

  private _removeFile(event: Event) {
    event.preventDefault();

    if (event.currentTarget instanceof HTMLElement) {
      const fileId = event.currentTarget.dataset.fileId;

      if (this._dropzone) {
        const fileToRemove = this._dropzone
          .getAcceptedFiles()
          .find((f) => f.upload?.uuid === fileId);

        if (fileToRemove) {
          this._dropzone.removeFile(fileToRemove);
        }
      }
    }
  }

  private _addPreviewFiles() {
    if (this._validateTotalFileSize()) {
      this._updateHiddenInput();
      this._renderFileList();
      return;
    }

    fireToast(
      `Total file size limit exceeds the limit of ${this.maxSizeValue} MiB`,
      "danger"
    );
  }

  private _removePreviewFile(file: DropzoneFile) {
    this._updateHiddenInput();

    if (file.upload?.uuid) {
      this._removeFileNode(file.upload.uuid);
    }
  }

  private _onError(error: string | Error) {
    fireToast(error instanceof Error ? error.message : error, "danger");
  }

  private _validateTotalFileSize() {
    if (this._dropzone) {
      let totalFileSize = 0;

      return !this._dropzone.getAcceptedFiles().some((file) => {
        totalFileSize += file.size;

        return totalFileSize > this.maxSizeValue * this._megaByteToBytes;
      });
    }

    return false;
  }

  private _updateHiddenInput() {
    if (this._dropzone) {
      const dataTransfer = new DataTransfer();
      this._dropzone.getAcceptedFiles().forEach((file) => {
        dataTransfer.items.add(file);
      });

      if (this.hasFilesInputTarget) {
        this.filesInputTarget.files = dataTransfer.files;
      }
    }
  }

  private _removeFileNode(uuid: string) {
    if (this._dropzone && this._selectedFilesListContainer) {
      const acceptedFiles = this._dropzone.getAcceptedFiles();
      const { hasNonImages, hasImages } =
        this._checkHasFileTypes(acceptedFiles);

      const container = this._getFilesListContainer(
        this._selectedFilesListContainer
      );

      if (container) {
        const fileContainers = container.querySelectorAll(
          `[data-file-uuid="${uuid}"]`
        );
        if (fileContainers.length > 0) {
          fileContainers.forEach((fileContainer) => {
            fileContainer.remove();
          });
        }
      }

      this._removeOrCreateFileListContainer(
        this._selectedFilesListContainer,
        hasNonImages,
        hasImages
      );
    }
  }

  private _renderFileList() {
    if (this._dropzone) {
      const acceptedFiles = this._dropzone.getAcceptedFiles();

      const { hasNonImages, hasImages } =
        this._checkHasFileTypes(acceptedFiles);

      const { nonImagesFileListContainer, imagesFilesListContainer } =
        this._renderFilesListContainer(hasNonImages, hasImages);

      acceptedFiles.forEach((file) => {
        if (this._isImageFile(file) && imagesFilesListContainer) {
          this._renderImageItem(file, imagesFilesListContainer);
          return;
        }

        if (!this._isImageFile(file) && nonImagesFileListContainer) {
          this._renderFileItem(file, nonImagesFileListContainer);
        }
      });
    }
  }

  private _checkHasFileTypes(files: DropzoneFile[]) {
    const hasNonImages = files.some((file) => !this._isImageFile(file));
    const hasImages = files.some((file) => this._isImageFile(file));

    return { hasNonImages, hasImages };
  }

  private _renderFilesListContainer(hasNonImages: boolean, hasImages: boolean) {
    let nonImagesFilesListContainer: Element | null = null;
    let imagesFilesListContainer: Element | null = null;

    if (this._selectedFilesListContainer) {
      const container = this._removeOrCreateFileListContainer(
        this._selectedFilesListContainer,
        hasNonImages,
        hasImages
      );

      if (container) {
        nonImagesFilesListContainer =
          this._getNonImagesFilesListContainer(container);
        nonImagesFilesListContainer =
          this._removeOrCreateNonImagesFilesListContainer(
            nonImagesFilesListContainer,
            hasNonImages
          );

        if (nonImagesFilesListContainer) {
          container.appendChild(nonImagesFilesListContainer);
        }

        imagesFilesListContainer = this._getImagesFilesListContainer(container);
        imagesFilesListContainer = this._removeOrCreateImagesFilesListContainer(
          imagesFilesListContainer,
          hasImages
        );

        if (imagesFilesListContainer) {
          container.appendChild(imagesFilesListContainer);
        }
      }
    }

    return {
      nonImagesFileListContainer: nonImagesFilesListContainer,
      imagesFilesListContainer: imagesFilesListContainer,
    };
  }

  private _getFilesListContainer(selectedFilesListContainer: Element) {
    return selectedFilesListContainer.querySelector(".files-list__container");
  }

  private _getNonImagesFilesListContainer(filesListContainer: Element) {
    return filesListContainer.querySelector(
      ".non-images-files-list__container"
    );
  }

  private _getImagesFilesListContainer(filesListContainer: Element) {
    return filesListContainer.querySelector(".images-files-list__container");
  }

  private _removeOrCreateFileListContainer(
    selectedFilesListContainer: HTMLElement,
    hasNonImages: boolean,
    hasImages: boolean
  ) {
    let container = this._getFilesListContainer(selectedFilesListContainer);

    if (container && !hasNonImages && !hasImages) {
      container.remove();
    }

    if (!container && (hasNonImages || hasImages)) {
      container = document.createElement("div");
      container.classList.add("files-list__container");
      selectedFilesListContainer.appendChild(container);

      const uploadedFilesLabel = document.createElement("span");
      uploadedFilesLabel.innerText = "Selected Files";
      uploadedFilesLabel.classList.add(
        "fs-7",
        "fw-semibold",
        "text-x-gray-800"
      );
      container.appendChild(uploadedFilesLabel);
    }

    return container;
  }

  private _removeOrCreateNonImagesFilesListContainer(
    nonImagesFilesListContainer: Element | null,
    hasNonImages: boolean
  ) {
    if (nonImagesFilesListContainer && !hasNonImages) {
      nonImagesFilesListContainer.remove();
    }

    if (!nonImagesFilesListContainer && hasNonImages) {
      nonImagesFilesListContainer = document.createElement("div");
      nonImagesFilesListContainer.classList.add(
        "non-images-files-list__container"
      );
    }

    return nonImagesFilesListContainer;
  }

  private _removeOrCreateImagesFilesListContainer(
    imagesFilesListContainer: Element | null,
    hasImages: boolean
  ) {
    if (imagesFilesListContainer && !hasImages) {
      imagesFilesListContainer.remove();
    }

    if (!imagesFilesListContainer && hasImages) {
      imagesFilesListContainer = document.createElement("div");
      imagesFilesListContainer.classList.add("images-files-list__container");
    }

    return imagesFilesListContainer;
  }

  private _isImageFile(file: DropzoneFile) {
    return ["image/jpeg", "image/png"].includes(file.type);
  }

  private _renderFileItem(file: DropzoneFile, container: Element) {
    const existNode = container.querySelector(
      `[data-file-uuid="${file.upload?.uuid}"]`
    );
    if (existNode) return;

    const nonImageItem = document.createElement("div");
    nonImageItem.classList.add("non-image-file");
    nonImageItem.dataset.fileUuid = file.upload?.uuid;

    const nonImageIconContainer = document.createElement("div");
    nonImageIconContainer.classList.add("file-icon");
    const nonImageIcon = document.createElement("i");
    nonImageIcon.classList.add("bi", "bi-file-text-fill", "d-flex");
    nonImageIconContainer.appendChild(nonImageIcon);
    nonImageItem.appendChild(nonImageIconContainer);

    const fileDetailContainer = document.createElement("div");
    fileDetailContainer.classList.add("file-detail");
    const nameSpan = document.createElement("span");
    nameSpan.classList.add("fs-7", "text-x-gray-800");
    nameSpan.textContent = file.name;
    fileDetailContainer.appendChild(nameSpan);
    const sizeSpan = document.createElement("span");
    sizeSpan.classList.add("fs-8", "text-x-gray-500");
    sizeSpan.textContent = `${(file.size / this._megaByteToBytes).toFixed(
      1
    )} MiB`;
    fileDetailContainer.appendChild(sizeSpan);
    nonImageItem.appendChild(fileDetailContainer);

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.classList.add("btn", "btn-light");
    removeButton.dataset.fileId = file.upload?.uuid;
    removeButton.onclick = this._removeFile.bind(this);
    const removeIcon = document.createElement("i");
    removeIcon.classList.add("bi", "bi-trash-fill", "d-flex");
    removeButton.appendChild(removeIcon);
    nonImageItem.appendChild(removeButton);

    container.appendChild(nonImageItem);
  }

  private _renderImageItem(file: DropzoneFile, container: Element) {
    const existNode = container.querySelector(
      `[data-file-uuid="${file.upload?.uuid}"]`
    );
    if (existNode) return;

    const imageFileItem = document.createElement("div");
    imageFileItem.classList.add("image-file-container");
    imageFileItem.dataset.fileUuid = file.upload?.uuid;

    const imageContainer = document.createElement("div");
    imageContainer.classList.add("image-container");

    const image = document.createElement("img");
    const reader = new FileReader();
    reader.onload = function (e) {
      if (e.target?.result && typeof e.target.result === "string") {
        image.src = e.target.result;
        image.alt = file.name;
      }
    };
    reader.readAsDataURL(file);
    imageContainer.appendChild(image);
    imageFileItem.appendChild(imageContainer);

    const fileDetailContainer = document.createElement("div");
    fileDetailContainer.classList.add("file-detail-container");

    const fileDetail = document.createElement("div");
    fileDetail.classList.add("file-detail");

    const nameSpan = document.createElement("span");
    nameSpan.classList.add(
      "fs-7",
      "text-x-gray-800",
      "text-truncate",
      "d-block"
    );
    nameSpan.textContent = file.name;
    nameSpan.title = file.name;
    fileDetail.appendChild(nameSpan);
    const sizeSpan = document.createElement("span");
    sizeSpan.classList.add("fs-8", "text-x-gray-500");
    sizeSpan.textContent = `${(file.size / this._megaByteToBytes).toFixed(
      2
    )} MiB`;
    fileDetail.appendChild(sizeSpan);
    fileDetailContainer.appendChild(fileDetail);
    imageFileItem.appendChild(fileDetailContainer);

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.classList.add("btn", "btn-light");
    removeButton.dataset.fileId = file.upload?.uuid;
    removeButton.onclick = this._removeFile.bind(this);
    const removeIcon = document.createElement("i");
    removeIcon.classList.add("bi", "bi-trash-fill", "d-flex");
    removeButton.appendChild(removeIcon);
    fileDetailContainer.appendChild(removeButton);

    container.appendChild(imageFileItem);
  }
}
